import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './PassengerForm.css';
import { Row, Col, Form } from 'react-bootstrap';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { SingleCalendar } from '../../../../components';
import { useTranslation } from '../../../../contexts/TranslationContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import debounce from 'lodash.debounce';

const PassengerForm = ({ index, onRemove, canRemove, onFormChange, formData = {}, locationCache, fetchLocations }) => {
    const { t } = useTranslation();

    const [searchResults, setSearchResults] = useState({
        countries: [],
        cities: [],
    });

    const [dropdownOptions, setDropdownOptions] = useState({
        nationality: [],
        residenceCountry: [],
        birthCountry: [],
        issuanceCountry: [],
        addressCountry: [],
    });

    // Static data for other dropdowns
    const staticTitles = [
        { name: "Mr", code: "Mr" },
        { name: "Mrs", code: "Mrs" },
        { name: "Ms", code: "Ms" },
        { name: "Madam", code: "Madam" },
        { name: "Miss", code: "Miss" },
        { name: "Master", code: "Master" },
        { name: "Mx", code: "Mx" },
        { name: "Dr", code: "Dr" },
        { name: "Prof", code: "Prof" },
    ];

    const staticPassengerTypes = [
        { name: "ADT", code: "ADT" },
        { name: "CHD", code: "CHD" },
        { name: "INF", code: "INF" },
    ];

    const staticDocumentTypes = [
        { name: "Passport", code: "Passport" },
        { name: "IQAMA", code: "IQAMA" },
        { name: "NationalId", code: "NationalId" },
    ];

    // Initialize form state with empty values or passed formData
    const [form, setForm] = useState({
        passengerType: formData.passengerType || '',
        title: formData.title || '',
        gender: formData.gender || '',
        firstName: formData.firstName || '',
        middleName: formData.middleName || '',
        lastName: formData.lastName || '',
        date_of_birth: formData.date_of_birth || '',
        nationality: formData.nationality || '',
        residenceCountry: formData.residenceCountry || '',
        documentNumber: formData.documentNumber || '',
        documentType: formData.documentType || '',
        expirationDate: formData.expirationDate || '',
        issuanceDate: formData.issuanceDate || '',
        birthCountry: formData.birthCountry || '',
        issuanceCountry: formData.issuanceCountry || '',
        email: formData.email || '',
        phoneNumber: formData.phoneNumber || '',
        addressCountry: formData.addressCountry || '',
        addressLine1: formData.addressLine1 || '',
        addressLine2: formData.addressLine2 || ''
    });


     const debouncedSearch = useMemo(() => 
        debounce(async (field, searchTerm) => {
            if (!searchTerm || searchTerm.length < 2) return;

            const url = `https://crm.alltimestravels.com/api/method/alltimes.www.api.locations?search=${encodeURIComponent(searchTerm)}`;
            const res = await fetch(url);
            const data = await res.json();

            if (data?.result?.locations) {
                const countries = [];
                const cities = [];
                const countryMap = new Map();

                data.result.locations.forEach((loc) => {
                    if (loc.country && loc.country_code) {
                        countryMap.set(loc.country_code, { 
                            name: loc.country, 
                            code: loc.country_code 
                        });
                    }
                    if (loc.location_name) {
                        cities.push({
                            name: loc.location_name,
                            code: loc.location_code || loc.location_name,
                            countryCode: loc.country_code,
                            countryName: loc.country
                        });
                    }
                });

                const newCountries = Array.from(countryMap.values());
                const newCities = cities;

                setDropdownOptions((prev) => ({
                    ...prev,
                    [field]: field === "cityCode" ? newCities : newCountries
                }));

                setSearchResults((prev) => ({
                    ...prev,
                    countries: [...prev.countries, ...newCountries],
                    cities: [...prev.cities, ...newCities]
                }));
            }
        }, 300),
        []
    );

    const handleDropdownSearch = useCallback((field, searchTerm) => {
        // Only search for location-related fields
        const locationFields = ["nationality", "residenceCountry", "birthCountry", "issuanceCountry", "addressCountry"];
        
        if (!locationFields.includes(field)) return;
        if (!searchTerm || searchTerm.length < 2) return;
        
        // Check if we already have this data cached
        const currentOptions = dropdownOptions[field] || [];
        const hasExactMatch = currentOptions.some(option => 
            option.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            option.code?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        if (!hasExactMatch) {
            debouncedSearch(field, searchTerm);
        }
    }, [debouncedSearch, dropdownOptions]);

    // Cleanup
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

   const getDisplayValue = (field) => {
        // First check if we have the display object stored
        if (form[`${field}Display`]) {
            return form[`${field}Display`];
        }

        const value = form[field];
       
        
        if (!value) return null;

        // For static fields
        if (field === 'title') {
            return staticTitles.find((t) => t.code === value || t.name === value) || null;
        } else if (field === 'documentType') {
            return staticDocumentTypes.find((t) => t.code === value || t.name === value) || null;
        } else if (field === 'passengerType') {
            return staticPassengerTypes.find((t) => t.code === value || t.name === value) || null;
        }

        // For dynamic location fields - existing logic
        let allOptions = [];
        if (["nationality", "residenceCountry", "birthCountry", "issuanceCountry", "addressCountry"].includes(field)) {
            allOptions = [
                ...locationCache.countries,
                ...searchResults.countries,
                ...dropdownOptions[field] || []
            ];
        } else if (field === "cityCode") {
            allOptions = [
                ...locationCache.cities,
                ...searchResults.cities,
                ...dropdownOptions[field] || []
            ];
        }

        const uniqueOptions = allOptions.filter((option, index, self) =>    
            index === self.findIndex((o) => o.code === option.code && o.name === option.name)
        );

        return uniqueOptions.find((option) => 
            option.code === value || option.name === value
        ) || null;
    };

    // Handle form field changes
   const debouncedFormChange = useMemo(() => debounce((index, updatedForm) => {
    onFormChange(index, updatedForm);
    }, 1500), [onFormChange]);

    const handleInputChange = (field, value) => {
        const updatedForm = { ...form, [field]: value };
        setForm(updatedForm);
        
        // Only call the debounced form change for actual form updates
        // Not for dropdown search interactions
        if (!field.includes('Display')) {
            debouncedFormChange(index, updatedForm);
        }
    };
    // Handle Autocomplete changes
    const handleAutocompleteChange = (field, value) => {
          
        
        // Store the entire option object in form state
        const fieldValue = value ? value.code || value.name || value : '';
        const updatedForm = {
            ...form,
            [field]: fieldValue,
            // Store the display object separately
            [`${field}Display`]: value
        };
        setForm(updatedForm);
        onFormChange(index, updatedForm);
    };


    const parseDateString = (dateString) => {
        if (!dateString) return null;
        // If it's already a Date object, return it
        if (dateString instanceof Date) return dateString;
        // Otherwise, parse the string
        return new Date(dateString);
    };
    
    // Update form when formData changes
    useEffect(() => {
        if (formData) {
            setForm(prevForm => ({
                ...prevForm,
                ...formData
            }));
        }
    }, [formData]);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
            debouncedFormChange.cancel();
        };
    }, [debouncedSearch, debouncedFormChange]);

    // Get display value for Autocomplete
   

  

    return (
        <div className='passenger-container'>
            <div className='passenger-index'>Passenger {index + 1}</div>
            {canRemove && (
                <div className='passenger-delete' onClick={onRemove}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <path d="M12.25 9.625V14.875M8.75 9.625V14.875M5.25 6.125V16.625C5.25 17.0891 5.43437 17.5342 5.76256 17.8624C6.09075 18.1906 6.53587 18.375 7 18.375H14C14.4641 18.375 14.9092 18.1906 15.2374 17.8624C15.5656 17.5342 15.75 17.0891 15.75 16.625V6.125M3.5 6.125H17.5M6.125 6.125L7.875 2.625H13.125L14.875 6.125" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            )}

            <Form>
                <Row className='passenger-form-row'>
                    <div className='passenger-form-title'>Personal Information</div>
                    
                    {/* Passenger Type Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-passenger-type">
                            <label className="hotel-form-input-label">
                                Passenger Type Code <span>*</span>
                            </label>
                            <Autocomplete
                                options={staticPassengerTypes}
                                getOptionLabel={(option) => option.name || ""}
                                value={getDisplayValue('passengerType')}
                                onChange={(event, value) => handleAutocompleteChange('passengerType', value)}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select Passenger Type"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>

                    {/* Title */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-title">
                            <label className="hotel-form-input-label">
                                Title <span>*</span>
                            </label>
                            <Autocomplete
                                options={staticTitles}
                                getOptionLabel={(option) => option.name || ""}
                                value={getDisplayValue('title')}
                                onChange={(event, value) => handleAutocompleteChange('title', value)}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select Title"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>

                    {/* Gender */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-gender">
                            <label className="hotel-form-input-label">
                                {t("Gender")} <span>*</span>
                            </label>
                            <Form.Select
                                size="sm"
                                value={form.gender}
                                onChange={(e) => handleInputChange('gender', e.target.value)}
                                className="hotel-form-input select-dropdown"
                            >
                                <option value="" disabled>
                                    {t("SelectGender")}
                                </option>
                                <option value="Male">{t("Male")}</option>
                                <option value="Female">{t("Female")}</option>
                            </Form.Select>
                        </div>
                    </Col>

                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>First Name <span>*</span></label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                placeholder="First Name"
                            />
                        </div>
                    </Col>
                    
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Middle Name</label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.middleName}
                                onChange={(e) => handleInputChange('middleName', e.target.value)}
                                placeholder="Middle Name"
                            />
                        </div>
                    </Col>
                    
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Last Name <span>*</span></label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                placeholder="Last Name"
                            />
                        </div>
                    </Col>

                    {/* Date of Birth */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-date_of_birth">
                            <label className="hotel-form-input-label">
                                {t("DateOfBirth")} <span>*</span>
                            </label>
                            <SingleCalendar
                                value={form.date_of_birth}
                                onChange={(e) => handleInputChange('date_of_birth', e.value)} 
                                placeholder={t("DateOfBirth")}
                                showTime={false}
                                max={new Date()}
                            />
                        </div>
                    </Col>

                    {/* Nationality Country Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-nationality">
                            <label className="hotel-form-input-label">
                                Nationality Country Code <span>*</span>
                            </label>
                            <Autocomplete
                                filterOptions={(x) => x} 
                                getOptionLabel={(option) => option.name || ""}
                                 options={
                                    dropdownOptions.nationality.length > 0
                                    ? dropdownOptions.nationality
                                    : locationCache.countries
                                }
                                onOpen={() => {
                                    if (
                                    locationCache.countries.length === 0 &&
                                    dropdownOptions.nationality.length === 0
                                    ) {
                                    fetchLocations(""); // من الأب
                                    }
                                }}
                                onInputChange={(e, val, reason) => {
                                    // Only search when user is typing, not when clearing or selecting
                                    if (reason === 'input') {
                                        handleDropdownSearch("nationality", val);
                                    }
                                }}
                                value={getDisplayValue("nationality")}
                                onChange={(e, value) => handleAutocompleteChange("nationality", value)}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={"Select Nationality"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>

                    {/* Residence Country Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-residence-country">
                            <label className="hotel-form-input-label">
                                Residence Country Code <span>*</span>
                            </label>
                            <Autocomplete
                                getOptionLabel={(option) => option.name || ""}
                                filterOptions={(x) => x}
                                options={
                                    dropdownOptions.residenceCountry.length > 0
                                    ? dropdownOptions.residenceCountry
                                    : locationCache.countries
                                }
                                onOpen={() => {
                                    if (
                                    locationCache.countries.length === 0 &&
                                    dropdownOptions.residenceCountry.length === 0
                                    ) {
                                    fetchLocations(""); // من الأب
                                    }
                                }}
                                onInputChange={(e, val, reason) => {
                                    // Only search when user is typing, not when clearing or selecting
                                    if (reason === 'input') {
                                        handleDropdownSearch("residenceCountry", val);
                                    }
                                }}
                                value={getDisplayValue("residenceCountry")}
                                onChange={(e, value) => handleAutocompleteChange("residenceCountry", value)}
                               
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={"Select Residence Country"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className='passenger-form-row'>
                    <div className='passenger-form-title'>Travel Document Information</div>
                    
                    {/* Document Number */}
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Document Number <span>*</span></label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.documentNumber}
                                onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                                placeholder="Document Number"
                            />
                        </div>
                    </Col>

                    {/* Document Type */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-document-type">
                            <label className="hotel-form-input-label">
                                Document Type <span>*</span>
                            </label>
                            <Autocomplete
                                options={staticDocumentTypes}
                                getOptionLabel={(option) => option.name || ""}
                                value={getDisplayValue('documentType')}
                                onChange={(event, value) => handleAutocompleteChange('documentType', value)}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select Document Type"
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>

                    {/* Expiration Date */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-expiration-date">
                            <label className="hotel-form-input-label">
                                Expiration Date <span>*</span>
                            </label>
                            <SingleCalendar
                                value={form.expirationDate}
                                onChange={(e) => handleInputChange('expirationDate', e.value)} 
                                placeholder={t("Expiration Date")}
                                showTime={false}
                                max={new Date()}
                            />
                        </div>
                    </Col>

                    {/* Issuance Date */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-issuance-date">
                            <label className="hotel-form-input-label">
                                Issuance Date
                            </label>
                            <SingleCalendar
                                value={form.issuanceDate}
                                onChange={(e) => handleInputChange('issuanceDate', e.value)} 
                                placeholder={t("Issuance Date")}
                                showTime={false}
                                max={new Date()}
                            />
                        </div>
                    </Col>

                    {/* Birth Country Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-birth-country">
                            <label className="hotel-form-input-label">
                                Birth Country Code
                            </label>
                            <Autocomplete
                                filterOptions={(x) => x}
                                getOptionLabel={(option) => option?.name || ""}
                                options={
                                    dropdownOptions.birthCountry.length > 0
                                    ? dropdownOptions.birthCountry
                                    : locationCache.countries
                                }
                                onOpen={() => {
                                    if (
                                    locationCache.countries.length === 0 &&
                                    dropdownOptions.birthCountry.length === 0
                                    ) {
                                    fetchLocations(""); // من الأب
                                    }
                                }}
                                onInputChange={(e, val, reason) => {
                                    // Only search when user is typing, not when clearing or selecting
                                    if (reason === 'input') {
                                        handleDropdownSearch("birthCountry", val);
                                    }
                                }}
                                value={getDisplayValue("birthCountry")}
                                onChange={(e, value) => handleAutocompleteChange("birthCountry", value)}
                                
                              
                                 
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={ "Birth Country Code"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>

                    {/* Issuance Country Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-issuance-country">
                            <label className="hotel-form-input-label">
                                Issuance Country Code
                            </label>
                            <Autocomplete
                                filterOptions={(x) => x}
                                getOptionLabel={(option) => option?.name || ""}
                                options={
                                    dropdownOptions.issuanceCountry.length > 0
                                    ? dropdownOptions.issuanceCountry
                                    : locationCache.countries
                                }
                                onOpen={() => {
                                    if (
                                    locationCache.countries.length === 0 &&
                                    dropdownOptions.issuanceCountry.length === 0
                                    ) {
                                    fetchLocations(""); // من الأب
                                    }
                                }}
                                onInputChange={(e, val, reason) => {
                                    // Only search when user is typing, not when clearing or selecting
                                    if (reason === 'input') {
                                        handleDropdownSearch("issuanceCountry", val);
                                    }
                                }}
                                value={getDisplayValue("issuanceCountry")}
                                onChange={(e, value) => handleAutocompleteChange("issuanceCountry", value)}
                                   
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={"Issuance Country Code"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className='passenger-form-row'>
                    <div className='passenger-form-title'>Contact Information</div>

                    {/* Email */}
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Email</label>
                            <Form.Control
                                className='hotel-form-input'
                                type='email'
                                value={form.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="Email Address"
                            />
                        </div>
                    </Col>

                    {/* Phone Number */}
                    <Col xl={4} md={6}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Phone Number</label>
                            <PhoneInput
                                international
                                countryCallingCodeEditable={false}
                                defaultCountry="US"
                                value={form.phoneNumber}
                                onChange={(value) => handleInputChange('phoneNumber', value)}
                                className="hotel-form-input"
                             
                            />
                        </div>
                    </Col>
                </Row>

                <Row className='passenger-form-row'>
                    <div className='passenger-form-title'>Address Information</div>

                    {/* Country Code */}
                    <Col xl={4} md={6}>
                        <div className="hotel-form-input-container" id="field-address-country">
                            <label className="hotel-form-input-label">
                                Country Code
                            </label>
                            <Autocomplete
                                getOptionLabel={(option) => option.name || ""}
                                filterOptions={(x) => x}
                               
                                options={
                                    dropdownOptions.addressCountry.length > 0
                                    ? dropdownOptions.addressCountry
                                    : locationCache.countries
                                }
                                onOpen={() => {
                                    if (
                                    locationCache.countries.length === 0 &&
                                    dropdownOptions.addressCountry.length === 0
                                    ) {
                                    fetchLocations(""); // من الأب
                                    }
                                }}
                                onInputChange={(e, val, reason) => {
                                    // Only search when user is typing, not when clearing or selecting
                                    if (reason === 'input') {
                                        handleDropdownSearch("issuanceCountry", val);
                                    }
                                }}
                                value={getDisplayValue("addressCountry")}
                                onChange={(e, value) => handleAutocompleteChange("addressCountry", value)}
                                sx={{
                                    ".MuiAutocomplete-inputRoot": {
                                        fontSize: "12px",
                                    },
                                    ".MuiAutocomplete-endAdornment": {
                                        right: "9px !important",
                                    },
                                    ".MuiInputBase-input": {
                                        textAlign: "left",
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        placeholder={"Country Code"}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { direction: "ltr" },
                                            sx: {
                                                backgroundColor: "#FFF",
                                                borderRadius: "6px",
                                                padding: "9px 15px !important",
                                                fontFamily: '"Dmsans400"',
                                                fontSize: "14px !important",
                                                fontWeight: 400,
                                                letterSpacing: "0.25px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Col>
                    
                    

                    <Col xl={12}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Address Line 1</label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.addressLine1}
                                onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                                placeholder="Address Line 1"
                            />
                        </div>
                    </Col>

                    <Col xl={12}>
                        <div className='hotel-form-input-container'>
                            <label className='hotel-form-input-label'>Address Line 2</label>
                            <Form.Control
                                className='hotel-form-input'
                                type='text'
                                value={form.addressLine2}
                                onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                                placeholder="Address Line 2"
                            />
                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default PassengerForm;